import React from 'react';
import { useNavigate } from 'react-router';

import styled from '@emotion/styled';

import { IGetCreatedJobs } from '../../models/dashboard/jobs/getCreatedJobs.model';
import { DASHBOARD, MANAGE_POSTED_JOBS } from '../../routes/routeConstants';
import { salaryInterval, timeAgo } from '../../utils/utils';
import DeleteIcon from '../Icons/DeleteIcon';
import JobsIcon from '../Icons/JobsIcon';
import Img from '../Img/Img';
import ViewApplicantStackedImageCard from './ViewApplicantStackedImageCard';
import { ModalProvider } from '../../context/ModalContext';
import ModalTriggerContainer from '../Modals/ModalTriggerContainer';
import { Modal } from '../Modals/Modal';
import DeletePostedJobModal from '../Modals/ModalsActions/DeletePostedJobModal';
import useDeleteJob from '../../hooks/dashboard/jobs/useDeleteJob';

interface ManageJobsCardProps {
  job: IGetCreatedJobs;
}

const ManagePostedJobsCard: React.FC<ManageJobsCardProps> = ({ job }) => {
  // Initialize the custom delete hook with the job id.
  const { deleteJob, loading } = useDeleteJob(job.id);

  const navigate = useNavigate();

  const handleDeleteJob = async () => {
    await deleteJob();
  };

  if (loading) {
    return (
      <Container>
        <p>Deleting</p>
      </Container>
    );
  }

  return (
    <Container
      onClick={() => navigate(`/${DASHBOARD}/${MANAGE_POSTED_JOBS}/${job.id}`)}
    >
      <ImageContainer>
        <Img
          // src={imgSrc}
          src={'/static/img/company-placeholder.png'}
          alt="shift location image"
        />
      </ImageContainer>

      <CenterContent>
        <CenterContentHeader>
          <CenterContentText>{job.title}</CenterContentText>
          <Description>{job.institutionName}</Description>
        </CenterContentHeader>

        <CenterContentLowerPart>
          <CenterContentFooter>
            <IconWrapper>
              <JobsIcon />
              {job.yearsOfExperience} year+
            </IconWrapper>

            <div>
              {job.pay.formatCurrency()}/{salaryInterval[job.payInterval] ?? ''}
            </div>
          </CenterContentFooter>
          <Date className="small-screen">
            Posted: <span>{timeAgo(job.createdAt)}</span>
          </Date>
          <ViewApplicant
          // onClick={() =>
          //   navigate(`/${DASHBOARD}/${MANAGE_POSTED_JOBS}/${job.id}`)
          // }
          >
            Applicants: <span>{job.applicationCount}</span>
          </ViewApplicant>
          {job.isPublished ? <Indicator1 /> : <Indicator2 />}
        </CenterContentLowerPart>
      </CenterContent>

      <StackedImages>
        <Date>
          Posted: <span>{timeAgo(job.createdAt)}</span>
        </Date>
        <ViewApplicantStackedImageCard
          navUrl={`/${DASHBOARD}/${MANAGE_POSTED_JOBS}/${job.id}`}
          count={job.applicationCount}
        />
      </StackedImages>
      <div onClick={(e) => e.stopPropagation()}>
        <ModalProvider>
          <ModalTriggerContainer>
            <StyledDel>
              <DeleteIcon />
            </StyledDel>
          </ModalTriggerContainer>
          <Modal>
            <DeletePostedJobModal proceedDel={handleDeleteJob} jobId={job.id} />
          </Modal>
        </ModalProvider>
      </div>
    </Container>
  );
};

export default ManagePostedJobsCard;

const Container = styled.div`
  display: grid;
  grid-template-columns: 100px auto 171px 32px;
  gap: 20px;
  width: 100%;
  align-items: center;
  /* border: 1px solid red; */
  box-shadow: 0px 20px 26px 0px rgba(186, 182, 182, 0.16);
  border-radius: 6px;
  padding: 25px 32px;
  position: relative;

  @media (max-width: 884px) {
    align-items: flex-start;
  }

  @media (max-width: 768px) {
    grid-template-columns: 100px auto 32px;
  }

  @media (max-width: 540px) {
    grid-template-columns: 50px auto 16px;
    gap: 8px;
    padding: 16px;
  }
`;

const ImageContainer = styled.div`
  width: 100px;
  height: 100px;

  @media (max-width: 540px) {
    width: 50px;
    height: 50px;
  }
`;

const CenterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CenterContentHeader = styled.div`
  display: flex;
  flex-direction: column;
`;

const CenterContentText = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.blackBlack2};

  @media (max-width: 768px) {
    font-size: 16px;
    font-weight: 500;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const Description = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.greyGrey1};

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const CenterContentFooter = styled.div`
  display: flex;
  gap: 24px;
  color: ${({ theme }) => theme.palette.greyGrey1};
  font-size: 14px;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 12px;
    gap: 8px;
  }
`;

const CenterContentLowerPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-items: center;

  & > svg {
    width: 14px;
    height: 14px;
  }
`;

const StackedImages = styled.div`
  /* border: 1px solid red; */
  display: flex;
  flex-direction: column;
  justify-content: end;
  align-items: end;
  gap: 8px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ViewApplicant = styled.div`
  color: ${({ theme }) => theme.palette.mainBlue};
  font-size: 14px;
  cursor: pointer;

  & > span {
    font-weight: 500;
  }

  @media (min-width: 768px) {
    display: none;
  }
`;

const Indicator1 = styled.div`
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.stateColorGreen};
  position: absolute;
  top: 10px;
  right: 10px;
`;

const Indicator2 = styled.div`
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.greyGrey2};
  position: absolute;
  top: 10px;
  right: 10px;
`;

const StyledDel = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  width: 1rem;
`;

const Date = styled.p`
  display: flex;
  gap: 8px;
  color: ${({ theme }) => theme.palette.mainBlue};
  font-size: 14px;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 12px;
  }

  @media (min-width: 768px) {
    &.small-screen {
      display: none;
    }
  }
`;
